/**
 * Kikirie behaviour layer for Horizon: kit-card add to cart, tabs, level filter, compare slider.
 * Plain custom elements; markup works without JavaScript (forms post to /cart/add, all plates visible).
 */
import { CartLinesUpdateEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Wraps a product form on a kit card. Adds to cart through Horizon's cart event so the
 * drawer and the cart count update, then stamps the seal onto the card.
 */
class KkAddToCart extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const form = this.querySelector('form');
    form?.addEventListener('submit', this.#onSubmit, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const button = form.querySelector('button[type="submit"]');
    const error = this.querySelector('[data-kk-error]');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;

    const formData = new FormData(form);
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const quantity = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    button.setAttribute('aria-busy', 'true');
    if (error) error.hidden = true;

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body: formData })).then(
        (res) => res.json()
      );
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
      const failed = Boolean(response.status);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'kk-add-to-cart',
          sourceId: this.id || form.id,
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: failed ? undefined : response.sections,
          didError: failed,
        },
      });

      if (failed) {
        if (error) {
          error.textContent = response.description || response.message || this.dataset.errorText || '';
          error.hidden = false;
        }
        return;
      }

      button.classList.add('is-added');
      button.textContent = this.dataset.addedText || button.textContent;
      this.#stamp();
    } catch (e) {
      deferred.reject(e);
      if (error) {
        error.textContent = this.dataset.errorText || '';
        error.hidden = false;
      }
    } finally {
      button.removeAttribute('aria-busy');
    }
  };

  #stamp() {
    const target = this.closest('.kk-kitcard')?.querySelector('.kk-arch');
    const src = this.dataset.sealSrc;
    if (!target || !src || target.querySelector('.kk-kitcard__stamp')) return;
    const seal = document.createElement('img');
    seal.className = 'kk-kitcard__stamp';
    seal.src = src;
    seal.alt = '';
    seal.width = 46;
    seal.height = 46;
    target.append(seal);
  }
}

/**
 * Tabs: buttons with role="tab" and aria-controls; panels with role="tabpanel".
 * Arrow keys move between tabs.
 */
class KkTabs extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @param {HTMLButtonElement} tab */
  select(tab) {
    for (const other of this.tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls') || '');
      if (panel) panel.hidden = !selected;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (tab instanceof HTMLButtonElement) this.select(tab);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const tabs = this.tabs;
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (document.activeElement));
    if (index < 0) return;
    const next = tabs[(index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length];
    if (!next) return;
    next.focus();
    this.select(next);
  };
}

/**
 * Level filter: chips with data-level ("all" or 1–5) show only plates with that data-level.
 */
class KkLevelFilter extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const chip = event.target instanceof Element ? event.target.closest('[data-level-chip]') : null;
    if (!(chip instanceof HTMLButtonElement)) return;
    const level = chip.dataset.levelChip;
    for (const other of this.querySelectorAll('[data-level-chip]')) {
      other.setAttribute('aria-pressed', String(other === chip));
    }
    const scope = this.closest('[data-kk-filter-scope]') || this.parentElement;
    for (const item of scope?.querySelectorAll('[data-level]') || []) {
      if (item instanceof HTMLElement) item.hidden = level !== 'all' && item.dataset.level !== level;
    }
  };
}

/**
 * Compare slider: a range input over two stacked images sets --cut on the container.
 */
class KkCompare extends HTMLElement {
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const input = this.querySelector('input[type="range"]');
    input?.addEventListener(
      'input',
      (event) => {
        const target = /** @type {HTMLInputElement} */ (event.target);
        this.style.setProperty('--cut', `${target.value}%`);
      },
      { signal: this.#abort.signal }
    );
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }
}

const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

define('kk-add-to-cart', KkAddToCart);
define('kk-tabs', KkTabs);
define('kk-level-filter', KkLevelFilter);
define('kk-compare', KkCompare);
